import { useEffect, useState } from "react";
import {
  Button, Table, Modal, Form, Input, Select, message,
  Spin, TreeSelect, Space, Tag, Tooltip,
  Flex,
} from "antd";
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  FileTextOutlined, ArrowUpOutlined, ArrowDownOutlined,
  HistoryOutlined, SearchOutlined, BarChartOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { fetchProduct } from "../../../../configuration/module/Product/Service/product";
import { fetchAllBuildersByProductId } from "../../Configuration/ChecklistConfiguration/Service/checklistBuilder";
import {
  fetchBindings, postBinding, putBinding, deleteBinding, fetchBindingByUuid,
} from "../service/checklistBinding";
import api from "../../../../BasicComponent/Service/api";
import { CHECKLIST_BINDING_URL } from "../../../utils/constService";

const { Option } = Select;

const buildTreeData = (nodes) =>
  nodes.map((n) => ({
    title: n.productName, value: n.uuid, key: n.uuid,
    children: n.children ? buildTreeData(n.children) : [],
  }));

// ── Revision badge ─────────────────────────────────────────────────────────
const RevisionBadge = ({ revisionNo }) => (
  <Tag
    icon={<HistoryOutlined />}
    color={revisionNo > 1 ? "orange" : "default"}
    style={{ fontSize: 10, padding: "0 5px", marginLeft: 4 }}
  >
    Rev {revisionNo}
  </Tag>
);

const ChecklistBinding = () => {
  const navigate = useNavigate();
  const [bindings,  setBindings]  = useState([]);
  const [products,  setProducts]  = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [total,     setTotal]     = useState(0);
  const [page,      setPage]      = useState(1);
  const [size,      setSize]      = useState(50);
  const [search,    setSearch]    = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);

  const [modalOpen,   setModalOpen]   = useState(false);
  const [editingUuid, setEditingUuid] = useState(null);
  const [saving,      setSaving]      = useState(false);

  const [form] = Form.useForm();
  const [seqList,      setSeqList]      = useState([]);
  const [modalProduct, setModalProduct] = useState(null);

  // ── Load ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchProduct().then((r) => setProducts(buildTreeData(r.data))).catch(() => {});
    loadBindings();
  }, []);

  // ── Search (debounced) ──────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => loadBindings(1, search, size, sortField, sortOrder), 500);
    return () => clearTimeout(t);
  }, [search]);

  const loadBindings = async (p = 1, s = search, sz = size, field = sortField, order = sortOrder) => {
    setLoading(true);
    try {
      const res = await fetchBindings({
        page: p - 1,
        size: sz,
        search: s || undefined,
        sort: field || undefined,
        desc: order === "descend",
      });
      setBindings(res.data?.content || []);
      setTotal(res.data?.totalElements || 0);
      setPage(p);
      setSize(sz);
      setSortField(field);
      setSortOrder(order);
    } finally { setLoading(false); }
  };

  // ── Modal: new build ──────────────────────────────────────────────────────
  const openAdd = () => {
    form.resetFields();
    setSeqList([]);
    setModalProduct(null);
    setTemplates([]);
    setEditingUuid(null);
    setModalOpen(true);
  };

  // ── Modal: edit build — warn if has executions ────────────────────────────
  const openEdit = async (uuid) => {
    setLoading(true);
    try {
      // Use allSettled so a missing has-executions endpoint doesn't crash the edit flow
      const [bindResult, execResult] = await Promise.allSettled([
        fetchBindingByUuid(uuid),
        api.get(`${CHECKLIST_BINDING_URL}/${uuid}/has-executions`),
      ]);

      if (bindResult.status === "rejected") {
        message.error("Failed to load binding data");
        return;
      }

      const b = bindResult.value.data;
      const hasExec = execResult.status === "fulfilled"
        ? execResult.value.data?.hasExecutions
        : false; // if endpoint missing, assume no executions — safe default

      setEditingUuid(uuid);
      setModalProduct(b.product?.uuid);
      form.setFieldsValue({ bindingName: b.bindingName, product: b.product?.uuid });

      const tr = await fetchAllBuildersByProductId(b.product?.uuid);
      setTemplates(tr.data?.data || []);
      setSeqList((b.items || []).map((item) => ({
        checklistId:   item.checklistId,
        checklistName: item.checklistName,
        revisionNo:    item.revisionNo || 1,
      })));
      setModalOpen(true);

      if (hasExec) {
        message.warning(
          `This binding has execution data. Saving will create a new revision (Rev ${(b.revisionNo || 1) + 1}).`,
          6
        );
      }
    } catch (e) {
      message.error("Failed to open edit: " + (e?.message || "unknown error"));
    } finally {
      setLoading(false);
    }
  };

  const handleProductChange = async (uuid) => {
    setModalProduct(uuid);
    setSeqList([]);
    if (!uuid) { setTemplates([]); return; }
    try {
      const res = await fetchAllBuildersByProductId(uuid);
      setTemplates(res.data?.data || []);
    } catch { setTemplates([]); }
  };

  const addToSeq = (checklistId) => {
    if (!checklistId) return;
    if (seqList.find((s) => s.checklistId === checklistId)) {
      message.warning("Already added"); return;
    }
    const tmpl = templates.find((t) => t.uuid === checklistId);
    setSeqList((prev) => [...prev, {
      checklistId,
      checklistName: tmpl?.checklistName || checklistId,
      revisionNo: tmpl?.revisionNo || 1,
    }]);
  };

  const removeFromSeq = (checklistId) =>
    setSeqList((prev) => prev.filter((s) => s.checklistId !== checklistId));

  const moveUp = (idx) => {
    if (idx === 0) return;
    setSeqList((prev) => {
      const n = [...prev];
      [n[idx - 1], n[idx]] = [n[idx], n[idx - 1]];
      return n;
    });
  };

  const moveDown = (idx) => {
    setSeqList((prev) => {
      if (idx >= prev.length - 1) return prev;
      const n = [...prev];
      [n[idx], n[idx + 1]] = [n[idx + 1], n[idx]];
      return n;
    });
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      // AntD already highlights the invalid fields; nothing else to do.
      return;
    }
    if (!seqList.length) { message.error("Add at least one checklist template to the sequence"); return; }
    setSaving(true);
    try {
      const payload = {
        productId:   values.product,
        bindingName: values.bindingName,
        items: seqList.map((s, i) => ({
          checklistId: s.checklistId,
          sequenceNo:  i + 1,
        })),
      };
      if (editingUuid) { await putBinding(editingUuid, payload); }
      else             { await postBinding(payload); }
      message.success(`Binding ${editingUuid ? "updated" : "created"} successfully`);
      setModalOpen(false);
      loadBindings();
    } catch (e) {
      message.error(e?.response?.data?.message || "Save failed");
    } finally { setSaving(false); }
  };

  // ── Delete with execution guard ───────────────────────────────────────────
  const handleDelete = async (uuid) => {
    try {
      await deleteBinding(uuid);
      message.success("Binding deleted");
      loadBindings();
    } catch (err) {
      const msg = err?.response?.data?.message || "Delete failed";
      if (err?.response?.status === 409) {
        Modal.error({
          title: "Cannot delete binding",
          icon: <HistoryOutlined style={{ color: "#f5222d" }} />,
          content: (
            <div style={{ fontSize: 13 }}>
              <p>{msg}</p>
              <p style={{ color: "#888", marginTop: 8 }}>
                Go to the Checklist menu, filter by the related templates,
                delete all executions, then come back to delete this binding.
              </p>
            </div>
          ),
          okText: "OK",
        });
      } else {
        message.error(msg);
      }
    }
  };

  // ── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "#", dataIndex: "id", width: 60, align: "center",
      render: (_, __, i) => (page - 1) * size + i + 1,
    },
    {
      title: "Binding Name", dataIndex: "bindingName", key: "bindingName",
      sorter: true,
      sortOrder: sortField === "bindingName" ? sortOrder : null,
      render: (v, record) => (
        <span>
          <strong>{v}</strong>
          <RevisionBadge revisionNo={record.revisionNo || 1} />
        </span>
      ),
    },
    {
      title: "Product", key: "product",
      dataIndex: ["product", "productName"],
      sorter: true,
      sortOrder: sortField === "product" ? sortOrder : null,
      render: (_, r) => r.product?.productName || "—",
    },
    {
      title: "Actions", key: "actions", width: 220, align: "center",
      render: (_, r) => (
        <Space>
          <Tooltip title="Report">
            <Button
              type="primary" size="small" icon={<FileTextOutlined />}
              onClick={() => navigate(`/check-list-builder/binding/report/${r.uuid}`)}
            >
              Report
            </Button>
          </Tooltip>
          <Tooltip title="Analysis of last 100 executions">
            <Button
              size="small" icon={<BarChartOutlined />}
              onClick={() => navigate(`/check-list-builder/binding/analysis/${r.uuid}`)}
            >
              Analysis
            </Button>
          </Tooltip>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r.uuid)} />
          <Button
            size="small" danger icon={<DeleteOutlined />}
            onClick={() => {
              Modal.confirm({
                title: "Delete this binding?",
                content: "This action cannot be undone.",
                okText: "Delete", okButtonProps: { danger: true },
                onOk: () => handleDelete(r.uuid),
              });
            }}
          />
        </Space>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Spin spinning={loading}>
      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Input
          placeholder="Search binding name or product"
          prefix={<SearchOutlined />}
          allowClear
          style={{ width: 280 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Flex justify="end" flex={1}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>New Binding</Button>
        </Flex>
      </div>

      <Table
        dataSource={bindings} columns={columns} rowKey="uuid" size="small"
        pagination={{
          current: page,
          total,
          pageSize: size,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
        }}
        onChange={(pagination, _, sorter) => {
          const field = sorter.order ? sorter.columnKey : null;
          const order = sorter.order || null;
          loadBindings(pagination.current, search, pagination.pageSize, field, order);
        }}
      />

      {/* Create / Edit modal */}
      <Modal
        open={modalOpen} title={editingUuid ? "Edit Binding" : "New Binding"}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave} okText="Save" confirmLoading={saving}
        width={580}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="product" label="Product" rules={[{ required: true }]}>
            <TreeSelect
              treeData={products} placeholder="Select product" allowClear
              onChange={handleProductChange}
            />
          </Form.Item>
          <Form.Item name="bindingName" label="Binding Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Full Inspection Binding" />
          </Form.Item>
        </Form>

        {/* Template sequence builder */}
        <div style={{ marginTop: 4 }}>
          <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
            Template Sequence
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <Select
              showSearch placeholder="Pick a template to add…"
              style={{ flex: 1 }}
              disabled={!modalProduct || !templates.length}
              filterOption={(input, opt) => opt.children.toLowerCase().includes(input.toLowerCase())}
              onSelect={addToSeq}
              value={null}
            >
              {templates
                .filter((t) => !seqList.find((s) => s.checklistId === t.uuid))
                .map((t) => (
                  <Option key={t.uuid} value={t.uuid}>
                    {t.checklistName}
                    {t.revisionNo > 1
                      ? ` — Rev ${t.revisionNo}`
                      : t.revisionNo === 1 ? " — Rev 1" : ""}
                    {t.isLatest ? " ✓" : ""}
                  </Option>
                ))}
            </Select>
          </div>

          {seqList.length === 0 ? (
            <div style={{ color: "#aaa", fontSize: 12, padding: "8px 0" }}>
              No templates added yet. Select from the dropdown above.
            </div>
          ) : (
            <div style={{ border: "1px solid #f0f0f0", borderRadius: 6, overflow: "hidden" }}>
              {seqList.map((item, idx) => (
                <div key={item.checklistId} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 12px",
                  background: idx % 2 === 0 ? "#fafafa" : "#fff",
                  borderBottom: idx < seqList.length - 1 ? "1px solid #f0f0f0" : "none",
                }}>
                  <Tag color="blue" style={{ minWidth: 26, textAlign: "center", fontWeight: 700 }}>
                    {idx + 1}
                  </Tag>
                  <span style={{ flex: 1, fontSize: 13 }}>
                    {item.checklistName}
                    <RevisionBadge revisionNo={item.revisionNo || 1} />
                  </span>
                  <Space size={2}>
                    <Button type="text" size="small" icon={<ArrowUpOutlined />}
                      disabled={idx === 0} onClick={() => moveUp(idx)} />
                    <Button type="text" size="small" icon={<ArrowDownOutlined />}
                      disabled={idx === seqList.length - 1} onClick={() => moveDown(idx)} />
                    <Button type="text" danger size="small" icon={<DeleteOutlined />}
                      onClick={() => removeFromSeq(item.checklistId)} />
                  </Space>
                </div>
              ))}
            </div>
          )}
        </div>
      </Modal>
    </Spin>
  );
};

export default ChecklistBinding;